import { Progress, Space, Typography } from 'antd5';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Alert, Button } from 'components/ui';
import type { SkydioImportStatus } from 'utils/skydio';

import { useSkydioImportStatusPolling } from './useSkydioImportStatusPolling';

const storageKey = 'horizon.skydioImportRecords.v1';
const storageEventName = 'horizon-skydio-import-records-changed';

type StoredImportRecord = {
  bundleId: string;
  inspectionId: string;
  recordId: string;
  selectedCount: number;
  startedAt: string;
};

type ImportProgress = {
  addedCount?: number;
  description: string;
  failedCount?: number;
  message: string;
  percent?: number;
  retryable?: boolean;
  selectedCount: number;
  skippedCount?: number;
  status: 'pending' | 'success' | 'error';
  userAction?: string;
  updatedCount?: number;
};

const StatusAlert = styled(Alert)`
  .ant-alert-message {
    color: ${({ theme }) => theme.headerColor};
  }

  .ant-alert-description {
    color: ${({ theme }) => theme.textColor};
  }
`;

function readRecords(): Record<string, StoredImportRecord> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(([inspectionId, record]) => {
        if (!record || typeof record !== 'object') {
          return false;
        }
        return (
          record.inspectionId === inspectionId &&
          typeof record.recordId === 'string' &&
          record.recordId.length > 0 &&
          typeof record.bundleId === 'string' &&
          typeof record.startedAt === 'string' &&
          typeof record.selectedCount === 'number' &&
          Number.isFinite(record.selectedCount) &&
          record.selectedCount >= 0
        );
      })
    );
  } catch (_error) {
    return {};
  }
}

function writeRecords(records: Record<string, StoredImportRecord>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(records));
  } catch {
    // Browser persistence must not turn an accepted backend import into a failed submission.
    console.warn('Unable to persist Skydio import history in browser storage');
  }
  window.dispatchEvent(new CustomEvent(storageEventName));
}

export function saveSkydioImportRecord(record: StoredImportRecord) {
  writeRecords({
    ...readRecords(),
    [record.inspectionId]: record,
  });
}

export function clearSkydioImportRecord(inspectionId: string) {
  const records = readRecords();
  delete records[inspectionId];
  writeRecords(records);
}

function useStoredImportRecord(inspectionId: string) {
  const [record, setRecord] = useState<StoredImportRecord | undefined>(
    () => readRecords()[inspectionId]
  );

  useEffect(() => {
    const refresh = () => setRecord(readRecords()[inspectionId]);
    refresh();
    window.addEventListener(storageEventName, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(storageEventName, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [inspectionId]);

  return record;
}

export function getSkydioImportProgress({
  recordId,
  selectedCount,
  status,
  t,
}: {
  recordId?: string;
  selectedCount: number;
  status?: SkydioImportStatus;
  t: TFunction;
}): ImportProgress | undefined {
  if (!recordId) {
    return undefined;
  }

  const addedCount = status?.addedCount ?? 0;
  const failedCount = status?.failedCount ?? 0;
  const skippedCount = status?.skippedCount ?? 0;
  const updatedCount = status?.updatedCount ?? 0;
  const resolvedSelectedCount = status?.selectedCount ?? selectedCount;
  const completedUnits =
    status?.phase === 'SNAPSHOTTING' ? status.completedSnapshotPages : status?.completedBatches;
  const expectedUnits =
    status?.phase === 'SNAPSHOTTING' ? status.expectedSnapshotPages : status?.expectedBatches;
  const percent =
    typeof completedUnits === 'number' && typeof expectedUnits === 'number' && expectedUnits > 0
      ? Math.min(100, Math.round((completedUnits / expectedUnits) * 100))
      : undefined;

  if (status?.status === 'COMPLETE') {
    return {
      addedCount,
      description: t(
        'inspection.skydio.completedSummary',
        '{{addedCount}} added, {{updatedCount}} updated, {{skippedCount}} skipped',
        { addedCount, skippedCount, updatedCount }
      ),
      message: t('inspection.skydio.importComplete', 'Skydio import complete'),
      percent: 100,
      selectedCount: resolvedSelectedCount,
      skippedCount,
      status: 'success',
      updatedCount,
    };
  }

  if (status?.status === 'FAILED') {
    return {
      description:
        status.errorMessage ??
        t(
          'inspection.skydio.importFailedDescription',
          'The Skydio import failed before all selected media could be imported.'
        ),
      failedCount: failedCount || resolvedSelectedCount,
      message: status.errorCode
        ? t('inspection.skydio.importFailedCode', 'Skydio import failed: {{code}}', {
            code: status.errorCode,
          })
        : t('inspection.skydio.importFailed', 'Skydio import failed'),
      percent,
      retryable: status.errorRetryable ?? undefined,
      selectedCount: resolvedSelectedCount,
      status: 'error',
      userAction: status.errorUserAction ?? undefined,
    };
  }

  if (status?.recovering) {
    return {
      description:
        status.lastRecoveryReason ??
        t(
          'inspection.skydio.importRecoveringDescription',
          'Automatic recovery is retrying interrupted import work.'
        ),
      message: t('inspection.skydio.importRecovering', 'Recovering Skydio import'),
      percent,
      selectedCount: resolvedSelectedCount,
      status: 'pending',
    };
  }

  return {
    description:
      status?.phase === 'SNAPSHOTTING'
        ? t(
            'inspection.skydio.importSnapshottingDescription',
            'Preparing the selected Skydio media for import.'
          )
        : t(
            'inspection.skydio.importRunningDescription',
            '{{completedCount}} of {{batchCount}} import batches complete for {{selectedCount}} media.',
            {
              batchCount: status?.expectedBatches ?? 0,
              completedCount: status?.completedBatches ?? 0,
              selectedCount: resolvedSelectedCount,
            }
          ),
    message: t('inspection.skydio.importRunning', 'Skydio import running'),
    percent,
    selectedCount: resolvedSelectedCount,
    status: 'pending',
  };
}

export function SkydioImportStatusPanel({
  inspectionIds,
  onClear,
  progress,
  recordId,
}: {
  inspectionIds: string[];
  onClear?: () => void;
  progress?: ImportProgress;
  recordId?: string;
}) {
  const { t } = useTranslation();
  if (!recordId || !progress) {
    return null;
  }

  return (
    <StatusAlert
      type={
        progress.status === 'error' ? 'error' : progress.status === 'success' ? 'success' : 'info'
      }
      showIcon
      message={progress.message}
      description={
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text>{progress.description}</Typography.Text>
          {progress.percent !== undefined ? (
            <Progress
              aria-label={progress.message}
              percent={progress.percent}
              status={
                progress.status === 'error'
                  ? 'exception'
                  : progress.status === 'success'
                    ? 'success'
                    : 'active'
              }
            />
          ) : null}
          {progress.userAction ? <Typography.Text>{progress.userAction}</Typography.Text> : null}
          {progress.retryable !== undefined ? (
            <Typography.Text>
              {progress.retryable
                ? t('inspection.skydio.retryAvailable', 'Retry is available.')
                : t('inspection.skydio.retryRequiresChange', 'Retry requires a change first.')}
            </Typography.Text>
          ) : null}
          <Typography.Text>
            {t('inspection.skydio.recordLabel', 'Record {{recordId}}', { recordId })}
          </Typography.Text>
          <Space wrap>
            {inspectionIds.map(id => (
              <a key={id} href={`/inspections/${id}`} target="_blank" rel="noreferrer">
                {t('inspection.skydio.inspectionLabel', 'Inspection {{inspectionId}}', {
                  inspectionId: id,
                })}
              </a>
            ))}
          </Space>
          {onClear && progress.status !== 'pending' ? (
            <Button onClick={onClear}>{t('inspection.skydio.dismiss', 'Dismiss')}</Button>
          ) : null}
        </Space>
      }
    />
  );
}

export function InspectionSkydioImportStatusBanner({
  inspectionId,
  onComplete,
}: {
  inspectionId: string;
  onComplete?: () => void;
}) {
  const { t } = useTranslation();
  const storedRecord = useStoredImportRecord(inspectionId);
  const completedRecordId = useRef<string>();
  const { error, retry, status } = useSkydioImportStatusPolling(storedRecord?.recordId);
  const progress = useMemo(
    () =>
      getSkydioImportProgress({
        recordId: storedRecord?.recordId,
        selectedCount: storedRecord?.selectedCount ?? 0,
        status: status?.recordId === storedRecord?.recordId ? status : undefined,
        t,
      }),
    [status, storedRecord, t]
  );

  useEffect(() => {
    if (error) {
      return;
    }
    if (
      progress?.status === 'success' &&
      storedRecord?.recordId &&
      completedRecordId.current !== storedRecord.recordId
    ) {
      completedRecordId.current = storedRecord.recordId;
      onComplete?.();
    }
  }, [error, onComplete, progress?.status, storedRecord?.recordId]);

  return error ? (
    <StatusAlert
      type="error"
      showIcon
      message={t(
        'inspection.skydio.importMonitorError',
        'Unable to monitor Skydio import: {{message}}',
        { message: error.message }
      )}
      description={
        <Space>
          <Button onClick={retry}>
            {t('inspection.skydio.retryMonitoring', 'Retry monitoring')}
          </Button>
          <Button onClick={() => clearSkydioImportRecord(inspectionId)}>
            {t('inspection.skydio.dismiss', 'Dismiss')}
          </Button>
        </Space>
      }
    />
  ) : (
    <SkydioImportStatusPanel
      inspectionIds={[inspectionId]}
      onClear={() => clearSkydioImportRecord(inspectionId)}
      progress={progress}
      recordId={storedRecord?.recordId}
    />
  );
}
